// Cross-link mutations hook
// Create and delete cross-links between problem and solution spaces

import { useCallback, useState } from 'react'
import { dualSpaceApi } from '@/lib/api/client'
import type { CrossLink } from '@ispot/shared'

export function useCrossLinks(
  accountId: string,
  projectId: string,
  onMutated: () => void,
) {
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createLink = useCallback(async (data: {
    sourceType: string; sourceId: string
    targetType: string; targetId: string
    relation?: string; rationale?: string
  }) => {
    setIsCreating(true)
    setError(null)
    try {
      await dualSpaceApi.createLink(accountId, projectId, data)
      onMutated()
    } catch (err: any) {
      const msg = err?.message || 'Failed to create link'
      // Duplicate link is not an error for the user
      if (msg.includes('already exists')) return
      setError(msg)
    } finally {
      setIsCreating(false)
    }
  }, [accountId, projectId, onMutated])

  const deleteLink = useCallback(async (linkId: string) => {
    setError(null)
    try {
      await dualSpaceApi.deleteLink(accountId, projectId, linkId)
      onMutated()
    } catch (err: any) {
      setError(err?.message || 'Failed to delete link')
    }
  }, [accountId, projectId, onMutated])

  return { createLink, deleteLink, isCreating, error }
}
